import styles from './Timeline.module.scss';
import footerImage from '@components/images/timeline/timeline_footer.include.svg';
import Container from '@components/components/Container/Container';
import {
  SingleEvent,
  DoubleEvent,
} from '@components/components/Timeline/components/Event/Event';
import React, { useMemo } from 'react';
import { Button, FontAwesomeProIcon, Image } from '@wienenergiegithub/ui-next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const renderEventElement = (
  { spacer, events, ...other },
  isAnimated,
  containerDimension,
  isFirstItem,
  headingLevel,
) => {
  const spacerArray = Array.from({ length: spacer }, (_, index) => index);
  if (events.length === 2) {
    return (
      <DoubleEvent
        containerDimension={containerDimension}
        events={events}
        headingLevel={headingLevel}
        isAnimated={isAnimated}
        isFirstItem={isFirstItem}
        spacer={spacerArray}
        {...other}
      />
    );
  }
  return (
    <SingleEvent
      containerDimension={containerDimension}
      events={events}
      headingLevel={headingLevel}
      isAnimated={isAnimated}
      isFirstItem={isFirstItem}
      spacer={spacerArray}
      {...other}
    />
  );
};

const renderFooterElement = (
  footerType,
  lastEvent,
  isAnimated,
  containerDimension,
  headingLevel,
) => {
  const handleScroll = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };
  switch (footerType) {
    case 'organic':
      return (
        <Image
          alt="Stadt Wien Silhouette"
          className={styles.footerImage}
          src={footerImage}
        />
      );
    case 'button':
      return <Button onClick={handleScroll}>Zurück nach oben</Button>;
    case 'lastEvent':
      return renderEventElement(
        lastEvent,
        isAnimated,
        containerDimension,
        false,
        headingLevel,
      );
    default:
      return null;
  }
};

const Timeline = ({
  headingLevel,
  items: defaultItems,
  footerType,
  isAnimated,
  containerDimension,
}) => {
  const [items, lastItem] = useMemo(() => {
    if (footerType === 'lastEvent') {
      // slice out last event for footer if lastEvent footer type has been selected
      return [
        defaultItems.slice(0, defaultItems.length - 1),
        defaultItems.slice(defaultItems.length - 1)?.[0],
      ];
    }
    return [defaultItems, null];
  }, [defaultItems, footerType]);

  // return null if no items or no items with at least one event are passed on
  if (!defaultItems || defaultItems?.events?.length === 0) {
    return null;
  }

  return (
    <Container
      as="ol"
      className={classNames(styles.listContainer, {
        [styles.marginBottom]: footerType !== 'organic',
      })}
      size="full"
    >
      {/* Starting element dot */}
      <li
        aria-hidden
        className={classNames(styles.timelineStart, 'big-entrance-animation')}
      >
        <FontAwesomeProIcon
          className={styles.timelineStartIcon}
          icon="chevron-down"
          role="presentation"
        />
      </li>
      {/* Events */}
      {items.map((item, index) => {
        const isFirstItem = index === 0;
        if (item.events.length === 0) {
          return null;
        }
        return (
          <li className={styles.listItem} key={item.id}>
            {renderEventElement(
              item,
              isAnimated,
              containerDimension,
              isFirstItem,
              headingLevel,
            )}
          </li>
        );
      })}
      {/* Footer element */}
      <li
        className={classNames(styles.listItem, {
          [styles[lastItem?.entryPosition]]: lastItem?.entryPosition,
          [styles.footerLastEvent]: footerType === 'lastEvent',
          [styles[lastItem?.imageSize]]:
            lastItem?.hasImage && lastItem?.imageSize,
        })}
      >
        {renderFooterElement(
          footerType,
          lastItem,
          isAnimated,
          containerDimension,
          headingLevel,
        )}
      </li>
    </Container>
  );
};

Timeline.propTypes = {
  containerDimension: PropTypes.string,
  footerType: PropTypes.oneOf(['lastEvent', 'button', 'organic']),
  isAnimated: PropTypes.bool,
  // exact shape of item object varies
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
};

Timeline.defaultProps = {
  containerDimension: null,
  footerType: 'lastEvent',
  isAnimated: false,
};

export default Timeline;
